/* eslint-disable @typescript-eslint/ban-types */
import { z } from 'zod'
import { log } from '@s4l/monitoring'
import { Model } from './architecture'
import type { Market, Markets } from '../shared/domain/Market'
import { countryDataSource, CURRENCIES } from '../_datasource/CountryDatasource'
import { DomainError } from './DomainError'
import { ErrorsCode } from './ErrorsCode'

export type Currencies = (typeof ALLOWED_CURRENCY)[number] | (string & {})
export const ALLOWED_CURRENCY = CURRENCIES

const FROM_MARKET_TO_CURRENCY: Record<Markets, Currencies> = Object.values(
  countryDataSource
).reduce(
  (acc, config) => {
    if (!acc[config.market]) {
      acc[config.market] = config.currency
    }
    return acc
  },
  {} as Record<Markets, Currencies>
)

export const CurrencyValidation = z.object({
  value: z.string({ required_error: '[Currency] value is required' }),
})

export class Currency extends Model {
  static create({ value }: { value: Currencies }) {
    const { error } = CurrencyValidation.safeParse({ value })
    if (error) {
      log.warn({
        message: '[Currency]: Unknown currency',
        metadata: { currency: value },
      })
    }
    return new Currency(value)
  }

  static createOrUndefined({ value }: { value: Currencies | undefined }) {
    if (!value) return undefined

    return Currency.create({ value })
  }

  static fromMarket({ market }: { market: Market }) {
    if (!market.value) throw DomainError.withCodes(ErrorsCode.MISSING_MARKET)

    return Currency.create({ value: FROM_MARKET_TO_CURRENCY[market.value] })
  }

  static empty() {
    return new Currency('USD')
  }

  constructor(private readonly _value: string) {
    super()
  }

  get value() {
    return this._value
  }

  toJSON() {
    return { value: this.value }
  }
}
